import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  cn,
} from "@motleyhand/design"
import { $enum } from "ts-enum-util"
import type { Product } from "./model"
import { ProductId } from "./model"
import { getBase, getCart, getQuantity, useSelector } from "./slice"
import { ButtonLink, useDispatch } from "@/base"
import { Feature, useFeatureEnabledQuery } from "@/feature"
import { useGetProductsQuery } from "@/money/api"
import { addAddon, removeAddon, setBase } from "@/money/slice"
import { formatPrice, invariant } from "@/utils"

type Props = {
  product: Product
  page: "pricing" | "purchase"
}

export default function ProductCard({ product, page }: Props) {
  const base = useSelector(getBase)
  const { interval } = useSelector(getCart)
  const quantity = useSelector((s) => getQuantity(s, product.id))
  const dispatch = useDispatch()
  const { data: products, isLoading: isProductsLoading } = useGetProductsQuery()
  const { data: enterpriseFeature } = useFeatureEnabledQuery(Feature.Enterprise)

  const disabled =
    product.productType === "addon" && !product.forBases.includes(base)
  const resolvedQty = disabled ? 0 : quantity
  const isSelected = !!resolvedQty && page === "purchase"
  const unlockWith = disabled ? product.forBases[0] : undefined
  const spark = product.productType === "base" ? product.spark : null
  const isFree = product.monthlyPrice.amount === 0

  const handleSelectBase =
    (baseProductId = product.id) =>
    () => {
      invariant(products)
      dispatch(
        setBase({
          id: baseProductId,
          allowedAddons: products
            .filter(
              (p) =>
                p.productType === "addon" && p.forBases.includes(baseProductId),
            )
            .map(({ id }) => id),
        }),
      )
    }

  const handleAddonQty = (qty: number) => {
    dispatch(
      qty
        ? addAddon({
            productId: product.id,
            quantity: Math.max(0, Math.round(qty)),
          })
        : removeAddon(product.id),
    )
  }

  if (page === "pricing" && product.productType === "addon") {
    return null
  }
  if (!enterpriseFeature && product.id === ProductId.Enterprise) {
    return null
  }
  if (isProductsLoading) {
    return null
  }
  invariant(products)

  return (
    <Card
      spark={
        spark
          ? {
              colour: spark,
              active: isSelected,
            }
          : undefined
      }
      className={cn(
        "relative",
        isSelected && "bg-foreground/5 dark:bg-zinc-950",
      )}
      size={product.productType === "base" ? "default" : "sm"}
    >
      <CardHeader className={cn(disabled && "opacity-50")}>
        <CardTitle>{product.name}</CardTitle>
        <CardDescription className="min-h-10">
          {product.tagline}
        </CardDescription>
      </CardHeader>
      <CardContent className={cn("flex-1", disabled && "opacity-50")}>
        <p className="text-lg font-bold">
          {formatPrice(
            (interval === "month"
              ? product.monthlyPrice.amount
              : product.yearlyPrice.amount) / 100,
            0,
          )}
        </p>
        <ul className="my-6 ml-6 list-['◆_'] [&_::marker]:text-muted-foreground [&>li]:mt-2">
          {product.description.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </CardContent>
      <CardFooter className={cn(disabled && "opacity-50")}>
        {product.productType === "base" &&
          page === "pricing" &&
          (isFree ? (
            <ButtonLink className="w-full" to="/dashboard">
              Start Free
            </ButtonLink>
          ) : (
            <ButtonLink
              className="w-full"
              to="/purchase"
              search={{ base: product.id }}
            >
              Get Started
            </ButtonLink>
          ))}
        {product.productType === "base" && page === "purchase" && (
          <Button
            className="w-full"
            onClick={handleSelectBase()}
            disabled={isSelected}
          >
            Select
          </Button>
        )}
        {product.productType === "addon" && product.isStackable && (
          <Input
            type="number"
            min={0}
            value={resolvedQty}
            onChange={(e) => {
              handleAddonQty(parseInt(e.target.value))
            }}
          />
        )}
        {product.productType === "addon" &&
          !product.isStackable &&
          (isSelected ? (
            <Button
              className="w-full"
              onClick={() => {
                handleAddonQty(0)
              }}
              variant="outline"
            >
              Remove
            </Button>
          ) : (
            <Button
              className="w-full"
              onClick={() => {
                handleAddonQty(1)
              }}
              variant="default"
            >
              Add
            </Button>
          ))}
      </CardFooter>
      {unlockWith && (
        <>
          <div className="pointer-events-auto absolute inset-0" />
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center pb-16">
            <Button
              className="pointer-events-auto shadow-md"
              onClick={handleSelectBase(unlockWith)}
            >{`Unlock with ${$enum(ProductId).getKeyOrThrow(unlockWith)}`}</Button>
          </div>
        </>
      )}
    </Card>
  )
}
